import React, { useCallback, forwardRef, useEffect, useState, useRef } from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PaletteRounded from "@mui/icons-material/PaletteRounded";
import WallpaperRounded from "@mui/icons-material/WallpaperRounded";
import ContentCutRounded from "@mui/icons-material/ContentCutRounded";
import LocationOnRounded from "@mui/icons-material/LocationOnRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import InsightsRounded from "@mui/icons-material/InsightsRounded";
import BadgeRounded from "@mui/icons-material/BadgeRounded";
import CreditCardRounded from "@mui/icons-material/CreditCardRounded";
import PhoneIphoneRounded from "@mui/icons-material/PhoneIphoneRounded";
import ShareRounded from "@mui/icons-material/ShareRounded";
import HandshakeRounded from "@mui/icons-material/HandshakeRounded";
import CardGiftcardRounded from "@mui/icons-material/CardGiftcardRounded";
import LockRounded from "@mui/icons-material/LockRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { useCookies } from "react-cookie";
import { googleLogout } from "@react-oauth/google";
import Account from "../../api/Account";
import { useNavigate } from "react-router-dom";
import { handlePushUnsubscription } from "../../utils/pushUtils";
import { previewRedirectHandler } from "../../utils/previewRedirectHandler";
import Brand from "../../api/Brand";
import { APP_MAX_WIDTH } from "../../constants/layout";
import { ANALYTICS_ENABLED } from "../../constants/features";
import useCurrentLanguage from "../../hooks/useCurrentLanguage";

interface MenuProps {
    onClose: () => void;
    isPartner?: boolean;
}

interface MenuItemDef {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    badge?: string;
}

interface MenuSection {
    title: string;
    items: MenuItemDef[];
}

const Menu = forwardRef<HTMLDivElement, MenuProps>((props, ref) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { onClose, isPartner = false } = props;

    const [cookies, , removeCookie] = useCookies([
        "uuid",
        "brand_id",
        "theme_color",
    ]);
    const { uuid, brand_id } = cookies;

    const [local, setLocal] = useState(false);
    const [affiliateEnabled, setAffiliateEnabled] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const currentLang = useCurrentLanguage();

    useEffect(() => {
        if (!uuid || !brand_id) return;

        Promise.all([Account.info(uuid), Brand.info(uuid, brand_id)])
            .then(([accountInfo, brandInfo]) => {
                setLocal(accountInfo?.local ?? false);
                setAffiliateEnabled(brandInfo?.affiliateEnabled ?? false);
            })
            .catch(console.error);
    }, [uuid, brand_id]);

    const onLogOut = useCallback(async () => {
        if (uuid) {
            try {
                await handlePushUnsubscription(uuid);
                await Account.signOut(uuid);
            } catch (err) {
                console.error(err);
            } finally {
                removeCookie("brand_id");
                removeCookie("theme_color");
                removeCookie("uuid");
                localStorage.removeItem("uuid");
                googleLogout();
            }
        }
        window.location.reload();
    }, [uuid, removeCookie]);

    const primary = theme.palette.primary.main;

    // Swipe-to-close: track horizontal swipe on the drawer panel
    const touchStartX = useRef<number | null>(null);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const isSwiping = useRef(false);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        isSwiping.current = false;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const dx = e.touches[0].clientX - touchStartX.current;
        // Only track rightward swipes (positive dx = toward right edge)
        if (dx > 10) isSwiping.current = true;
        if (isSwiping.current) {
            setSwipeOffset(Math.max(0, dx));
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (isSwiping.current && swipeOffset > 80) {
            onClose();
        }
        touchStartX.current = null;
        isSwiping.current = false;
        setSwipeOffset(0);
    }, [swipeOffset, onClose]);

    // Build sections conditionally
    const sections: MenuSection[] = [];

    if (!isPartner) {
        sections.push({
            title: "Customize",
            items: [
                {
                    label: "Color & Logo",
                    icon: <PaletteRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/brand"),
                },
                {
                    label: "Wallpaper",
                    icon: <WallpaperRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/wallpaper"),
                },
                {
                    label: "Edit Services",
                    icon: <ContentCutRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/services"),
                },
                {
                    label: "My Business Card",
                    icon: <BadgeRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/profile"),
                },
            ],
        });

        sections.push({
            title: "Business",
            items: [
                ...(ANALYTICS_ENABLED
                    ? [
                          {
                              label: "Analytics",
                              icon: <InsightsRounded sx={{ fontSize: 18 }} />,
                              onClick: () => navigate("/analytics"),
                          },
                      ]
                    : []),
                {
                    label: "Schedule & Location",
                    icon: <LocationOnRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/locations"),
                },
                {
                    label: "Automation",
                    icon: <TuneRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/automations"),
                },
                {
                    label: "Subscriptions & Payments",
                    icon: <CreditCardRounded sx={{ fontSize: 18 }} />,
                    onClick: () => navigate("/menu/subscriptions"),
                },
            ],
        });

        const loyaltyVisited = localStorage.getItem("loyalty_visited") === "true";
        sections.push({
            title: "Loyalty",
            items: [
                {
                    label: "Loyalty Program",
                    icon: <CardGiftcardRounded sx={{ fontSize: 18 }} />,
                    onClick: () => {
                        localStorage.setItem("loyalty_visited", "true");
                        navigate("/menu/loyalty");
                    },
                    badge: loyaltyVisited ? undefined : "New",
                },
            ],
        });

        const appItems: MenuItemDef[] = [
            {
                label: "My App",
                icon: <PhoneIphoneRounded sx={{ fontSize: 18 }} />,
                onClick: () => previewRedirectHandler(uuid, brand_id),
            },
            {
                label: "Share My App",
                icon: <ShareRounded sx={{ fontSize: 18 }} />,
                onClick: () => navigate("/menu/share"),
            },
        ];

        if (affiliateEnabled) {
            appItems.push({
                label: "Affiliate",
                icon: <HandshakeRounded sx={{ fontSize: 18 }} />,
                onClick: () => navigate("/partner"),
            });
        }

        sections.push({ title: "App", items: appItems });
    }

    if (local) {
        sections.push({
            title: "Account",
            items: [
                {
                    label: "Change Password",
                    icon: <LockRounded sx={{ fontSize: 18 }} />,
                    onClick: () =>
                        navigate("/menu/password", { state: { isPartner } }),
                },
                {
                    label: "Change Email",
                    icon: <EmailRounded sx={{ fontSize: 18 }} />,
                    onClick: () =>
                        navigate("/menu/email", { state: { isPartner } }),
                },
            ],
        });
    }

    // Flatten all items for stagger index
    let staggerIndex = 0;

    return (
        <Stack
            ref={ref}
            className="menu"
            direction="row"
            width="100%"
            maxWidth={APP_MAX_WIDTH}
            height="100dvh"
            position="fixed"
            right={0}
            left={0}
            marginX="auto"
            top={0}
            zIndex={20}
        >
            {/* Left side = backdrop */}
            <Box
                flex={1}
                onClick={onClose}
                sx={{
                    backgroundColor: `rgba(0, 0, 0, ${0.2 * Math.max(0, 1 - swipeOffset / 200)})`,
                    height: "100%",
                    transition: swipeOffset > 0 ? "none" : "background-color 0.2s ease-out",
                }}
            />

            {/* Menu drawer */}
            <Stack
                direction="column"
                width="70%"
                height="100%"
                bgcolor="white"
                spacing={0}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    pt: "calc(8px + env(safe-area-inset-top))",
                    "@keyframes menuSlideIn": {
                        from: { transform: "translateX(100%)" },
                        to: { transform: "translateX(0)" },
                    },
                    animation: swipeOffset === 0 ? "menuSlideIn 0.25s ease-out" : "none",
                    transform: swipeOffset > 0 ? `translateX(${swipeOffset}px)` : undefined,
                    transition: swipeOffset > 0 ? "none" : "transform 0.2s ease-out",
                }}
            >
                {/* Scrollable content */}
                <Stack
                    flex={1}
                    overflow="auto"
                    width="100%"
                    alignItems="flex-start"
                    spacing={0}
                >
                    {/* Language selector — top of menu */}
                    <LanguageSelector
                        currentLang={currentLang}
                        open={langOpen}
                        onToggle={() => setLangOpen((v) => !v)}
                        color={primary}
                        delay={0}
                    />

                    {/* Sections */}
                    {sections.map((section) => (
                        <Stack
                            key={section.title}
                            width="100%"
                            spacing={0}
                            mb={1.5}
                        >
                            {/* Section label */}
                            <Typography
                                className="force-dynamic-translation"
                                fontSize="10px"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing="0.8px"
                                px={2.5}
                                pt={1.5}
                                pb={0.75}
                            >
                                {section.title}
                            </Typography>

                            {/* Items */}
                            {section.items.map((item) => {
                                const idx = staggerIndex++;
                                return (
                                    <MenuItem
                                        key={item.label}
                                        label={item.label}
                                        icon={item.icon}
                                        onClick={item.onClick}
                                        color={primary}
                                        delay={idx * 30}
                                        badge={item.badge}
                                    />
                                );
                            })}
                        </Stack>
                    ))}
                </Stack>

                {/* Logout — pinned at bottom */}
                <Stack width="100%" flex="0 0 auto" pb={`calc(20px + env(safe-area-inset-bottom))`}>
                    <Box
                        sx={{
                            mx: 2.5,
                            mb: 1,
                            height: "1px",
                            bgcolor: "rgba(0,0,0,0.06)",
                        }}
                    />
                    <MenuItem
                        label="Log Out"
                        icon={<LogoutRounded sx={{ fontSize: 18 }} />}
                        onClick={onLogOut}
                        color="#d32f2f"
                        delay={staggerIndex * 30}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
});

const MenuItem = ({
    label,
    icon,
    onClick,
    color,
    delay,
    badge,
}: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    color: string;
    delay: number;
    badge?: string;
}) => (
    <ButtonBase
        onClick={onClick}
        className="force-dynamic-translation"
        sx={{
            width: "100%",
            textAlign: "start",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            px: 2.5,
            py: 0.875,
            opacity: 0,
            "@keyframes menuItemIn": {
                from: { opacity: 0, transform: "translateX(12px)" },
                to: { opacity: 1, transform: "translateX(0)" },
            },
            animation: `menuItemIn 0.25s ease-out ${delay}ms forwards`,
            "&:active": {
                bgcolor: "rgba(0,0,0,0.04)",
            },
        }}
    >
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                bgcolor: alpha(color, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                color: color,
                mr: 1.5,
            }}
        >
            {icon}
        </Box>
        <Typography
            fontWeight={500}
            fontSize="14px"
            lineHeight={1.3}
            color="text.primary"
        >
            {label}
        </Typography>
        {badge && (
            <Box
                sx={{
                    ml: 1,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: "4px",
                    bgcolor: color,
                    color: "white",
                    fontSize: "10px",
                    fontWeight: 700,
                    lineHeight: 1.4,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                }}
            >
                {badge}
            </Box>
        )}
    </ButtonBase>
);

const LanguageSelector = ({
    currentLang,
    open,
    onToggle,
    color,
    delay,
}: {
    currentLang: string;
    open: boolean;
    onToggle: () => void;
    color: string;
    delay: number;
}) => {
    const languages = React.useMemo(() => {
        if (!window.Weglot?.options) return [];
        const from = window.Weglot.options.language_from;
        const tos = window.Weglot.options.languages.map((l) => l.language_to);
        return [from, ...tos];
    }, []);

    const displayName = (code: string) =>
        window.Weglot?.getLanguageName?.(code) || code.toUpperCase();

    const handleSelect = (code: string) => {
        window.Weglot?.switchTo(code);
        onToggle();
    };

    return (
        <Stack
            width="100%"
            sx={{
                opacity: 0,
                "@keyframes menuItemIn": {
                    from: { opacity: 0, transform: "translateX(12px)" },
                    to: { opacity: 1, transform: "translateX(0)" },
                },
                animation: `menuItemIn 0.25s ease-out ${delay}ms forwards`,
            }}
        >
            {/* Trigger row */}
            <ButtonBase
                onClick={onToggle}
                sx={{
                    width: "100%",
                    textAlign: "start",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    px: 2.5,
                    py: 0.875,
                    "&:active": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
            >
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        bgcolor: alpha(color, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        color: color,
                        mr: 1.5,
                    }}
                >
                    <TranslateRounded sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                    fontWeight={500}
                    fontSize="14px"
                    lineHeight={1.3}
                    color="text.primary"
                    flex={1}
                >
                    {displayName(currentLang)}
                </Typography>
                <ExpandMoreRounded
                    sx={{
                        fontSize: 18,
                        color: "text.secondary",
                        transition: "transform 0.2s ease",
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                />
            </ButtonBase>

            {/* Dropdown */}
            <Stack
                sx={{
                    overflow: "hidden",
                    maxHeight: open ? `${languages.length * 40}px` : 0,
                    transition: "max-height 0.25s ease",
                }}
            >
                {languages.map((code) => (
                    <ButtonBase
                        key={code}
                        onClick={() => handleSelect(code)}
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            pl: 7.5,
                            pr: 2.5,
                            py: 0.75,
                            bgcolor:
                                code === currentLang
                                    ? alpha(color, 0.06)
                                    : "transparent",
                            "&:active": { bgcolor: alpha(color, 0.1) },
                        }}
                    >
                        <Typography
                            fontWeight={code === currentLang ? 600 : 400}
                            fontSize="13px"
                            color={
                                code === currentLang
                                    ? color
                                    : "text.primary"
                            }
                        >
                            {displayName(code)}
                        </Typography>
                    </ButtonBase>
                ))}
            </Stack>
        </Stack>
    );
};

export default Menu;
